<template>
    <div class="input-select-dropdown">
        <input
            :id="inputId"
            :type="inputType"
            :class="inputClass"
            v-model="localValue"
            v-bind="inputAttrs"
            @input="handleInput"
            @focus="showOptions"
            @blur="scheduleHide"
        />
        <select
            :id="selectId"
            v-show="visible"
            v-model="localValue"
            :class="selectClass"
            :size="selectSize"
            v-bind="selectAttrs"
            @focus="showOptions"
            @blur="scheduleHide"
            @change="handleSelect"
        >
            <option v-if="includeEmpty" value="" disabled></option>
            <option v-for="opt in filteredOptions" :key="optKey(opt)" :value="optValue(opt)">
                {{ optLabel(opt) }}
            </option>
        </select>
    </div>
</template>

<script>
    export default {
        name: 'InputSelectDropdown',
        props: {
            modelValue: {
                type: [String, Number, Boolean],
                default: '',
            },
            options: {
                type: Array,
                default: () => [],
            },
            inputId: {
                type: String,
                default: '',
            },
            selectId: {
                type: String,
                default: '',
            },
            inputType: {
                type: String,
                default: 'text',
            },
            inputClass: {
                type: [String, Array, Object],
                default: 'form-control form-control-sm',
            },
            selectClass: {
                type: [String, Array, Object],
                default: 'form-select form-select-sm',
            },
            inputAttrs: {
                type: Object,
                default: () => ({}),
            },
            selectAttrs: {
                type: Object,
                default: () => ({}),
            },
            optionValueKey: {
                type: String,
                default: 'value',
            },
            optionLabelKey: {
                type: String,
                default: 'text',
            },
            selectSize: {
                type: Number,
                default: 5,
            },
            includeEmpty: {
                type: Boolean,
                default: true,
            },
            closeOnSelect: {
                type: Boolean,
                default: true,
            },
        },
        watch: {
            modelValue(newVal) {
                this.searchText = this.getLabelForValue(newVal)
            },
        },
        emits: ['update:modelValue'],
        data() {
            return {
                visible: false,
                searchText: '',
            }
        },
        computed: {
            localValue: {
                get() {
                    return this.modelValue
                },
                set(val) {
                    this.$emit('update:modelValue', val)
                },
            },
            filteredOptions() {
                const term = (this.searchText || '').toString().toLowerCase().trim()
                if (!term) {
                    return this.options
                }
                return this.options.filter((opt) => {
                    const label = (this.optLabel(opt) || '').toString().toLowerCase()
                    return label.includes(term)
                })
            },
        },
        methods: {
            showOptions() {
                this.visible = true
                if (this._hideTimeout) {
                    clearTimeout(this._hideTimeout)
                }
            },
            hideOptions() {
                if (this._hideTimeout) {
                    clearTimeout(this._hideTimeout)
                }
                this.visible = false
            },
            handleInput(event) {
                this.searchText = event?.target?.value || ''
                this.showOptions()
            },
            scheduleHide() {
                if (this._hideTimeout) {
                    clearTimeout(this._hideTimeout)
                }
                this._hideTimeout = setTimeout(() => {
                    this.hideOptions()
                }, 150)
            },
            handleSelect() {
                if (this.closeOnSelect) {
                    this.hideOptions()
                }

                this.searchText = this.getLabelForValue(this.localValue)
            },
            optValue(opt) {
                return typeof opt === 'object' && opt !== null ? opt[this.optionValueKey] : opt
            },
            optLabel(opt) {
                return typeof opt === 'object' && opt !== null ? opt[this.optionLabelKey] : opt
            },
            optKey(opt) {
                const val = this.optValue(opt)
                return val != null ? val : JSON.stringify(opt)
            },
            getLabelForValue(val) {
                const match = this.options.find((opt) => this.optValue(opt) === val)
                if (match) {
                    return this.optLabel(match)
                }
                return val != null ? val.toString() : ''
            },
        },
    }
</script>

<style scoped>
    .input-select-dropdown {
        position: relative;
    }

    .input-select-dropdown input {
        border-color: var(--color-border-muted);
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
    }

    .input-select-dropdown input::placeholder {
        color: var(--color-text-muted);
    }

    .input-select-dropdown input:focus {
        border-color: var(--color-border-accent-strong);
        box-shadow: var(--shadow-focus-brand);
    }

    .input-select-dropdown select {
        position: absolute;
        top: calc(100% - 1px);
        left: 0;
        width: 100%;
        z-index: 5;
        height: 75px;
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
        box-shadow: var(--shadow-sm);
        border: 1px solid var(--color-border-muted);
        border-radius: 0.25rem;
    }

    .input-select-dropdown option {
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
    }

    .input-select-dropdown option:checked {
        background: var(--color-surface-subtle);
        color: var(--color-text-primary);
    }
</style>
